/**
 * luxury-gift-presentation.js v8
 * Gift Presentation ONLY (no message card). Craft theme (Dawn-based).
 *
 * v7 changes:
 * 1. Total follows the selected variant (was stuck on page-load price)
 * 2. Quantity included in summary total
 * 3. Correct product form used (page also has a Shop Pay installments form)
 * 4. Gift bow added BEFORE main product, so cart popup/count is correct.
 *    If the bow fails, the add stops instead of silently skipping the charge.
 * 5. Buy with Shop hidden while gift is ON (it bypasses the bow add)
 *
 * v8 changes:
 * 1. Null-safe gift summary row
 * 2. Payment buttons targeted inside the product form, note shown while hidden
 * 3. If the gift bow fails, gift is switched off so the customer can still add to bag
 *
 * v9 changes (Oct 2026):
 * 1. Express buttons are no longer hidden while gift is ON.
 *    A click on them now adds product + bow (with message) through the
 *    normal Add to Bag flow, then sends the shopper to the Shop Pay page
 *    (Buy with Shop) or to checkout (other buttons).
 *    With gift OFF the buttons work exactly as before.
 */

(function () {
  'use strict';

  var CFG = {
    giftHandle:     'satin-gift-bow',
    giftVariantId:  null,
    giftPricePence: 500,
  };

  var S = {
    giftOpen:       false,
    basePricePence: 0,
    touched:        false,
  };

  var D = {};

  var VARIANTS      = null;
  var lastVariantId = null;
  var syncTimer     = null;
  var PRICE_SUFFIX  = /\s*—\s*£[\d.,]+\s*$/;

  /* ══════════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════════ */
  function init() {
    D.root = document.getElementById('luxuryGiftPresentationBlock');
    if (!D.root) return;

    D.hdrGift      = document.getElementById('lgb-hdr-gift');
    D.bodyGift     = document.getElementById('lgb-body-gift');
    D.circGift     = document.getElementById('lgb-circle-gift');
    D.ctaGift      = document.getElementById('lgb-gift-cta');
    D.giftMsg      = document.getElementById('lgb-gift-msg');
    D.charN        = document.getElementById('lgb-char-n');
    D.giftBadge    = document.getElementById('lgb-gift-price-badge');
    D.sumBasePrice = document.getElementById('lgb-sum-base-price');
    D.sumBaseQty   = document.getElementById('lgb-sum-base-qty');
    D.sumGift      = document.getElementById('lgb-sum-gift');
    D.sumGiftPrice = document.getElementById('lgb-sum-gift-price');
    D.sumTotal     = document.getElementById('lgb-sum-total');

    S.basePricePence = parseInt(D.root.dataset.basePrice || '0', 10);

    bindEvents();
    fetchGiftProduct();
    loadVariantPrices();
    watchVariantChange();
    interceptAtcButton();
    interceptExpressButtons();
    updateSummary();
  }

  /* ══════════════════════════════════════════════════
     FORM HELPERS
  ══════════════════════════════════════════════════ */
  function atcForm() {
    var btn = document.querySelector('form[action*="/cart/add"] [name="add"]');
    return btn ? btn.closest('form') : document.querySelector('form[action*="/cart/add"]:not(.installment)');
  }

  function atcButton() {
    var form = atcForm();
    return form && form.querySelector('[type="submit"][name="add"], button[name="add"]');
  }

  function currentVariantId() {
    var form  = atcForm();
    var input = form && form.querySelector('[name="id"]');
    var id    = input ? parseInt(input.value, 10) : NaN;
    return isNaN(id) ? null : id;
  }

  function currentQty() {
    var q = document.querySelector('product-info input[name="quantity"]') ||
            document.querySelector('input[name="quantity"]');
    var n = q ? parseInt(q.value, 10) : 1;
    return (isNaN(n) || n < 1) ? 1 : n;
  }

  function currentTotal() {
    return S.basePricePence * currentQty() + (S.giftOpen ? CFG.giftPricePence : 0);
  }

  /* ══════════════════════════════════════════════════
     VARIANT SYNC
  ══════════════════════════════════════════════════ */
  async function loadVariantPrices() {
    try {
      var handle = D.root.dataset.productHandle ||
                   (window.location.pathname.split('/products/')[1] || '').split(/[?#]/)[0];
      if (!handle) return;
      var res = await fetch('/products/' + handle + '.js');
      if (!res.ok) throw new Error('HTTP ' + res.status);
      var data = await res.json();
      VARIANTS = {};
      data.variants.forEach(function (v) { VARIANTS[v.id] = v.price; });
      lastVariantId = null;
      syncBasePrice();
    } catch (e) {
      console.error('[LGB-Gift] variant prices failed:', e.message);
    }
  }

  function syncBasePrice() {
    var id = currentVariantId();
    if (!id || id === lastVariantId) return;
    lastVariantId = id;
    if (!VARIANTS || VARIANTS[id] == null) return;
    S.basePricePence = VARIANTS[id];
    D.root.dataset.basePrice = String(VARIANTS[id]);
    updateSummary();
  }

  function scheduleSync() {
    clearInterval(syncTimer);
    var tries = 0;
    syncTimer = setInterval(function () {
      syncBasePrice();
      if (++tries > 25) clearInterval(syncTimer);
    }, 100);
  }

  function onPageMutate() {
    syncBasePrice();
    updateAtcBtn(currentTotal());
  }

  function watchVariantChange() {
    var scope = document.querySelector('product-info') || atcForm();
    if (scope) {
      new MutationObserver(onPageMutate).observe(scope, { childList: true, subtree: true });
    }
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.name === 'quantity') { updateSummary(); return; }
      if (t.closest('variant-selects, variant-radios, .product-form__input')) scheduleSync();
    });
  }

  /* ══════════════════════════════════════════════════
     ATC INTERCEPT (only when gift is ON)
  ══════════════════════════════════════════════════ */
  function ensureGiftVariant() {
    return new Promise(function (resolve, reject) {
      var waited = 0;
      (function check() {
        if (CFG.giftVariantId) return resolve();
        if (waited >= 4000) return reject(new Error('Gift presentation is still loading. Please try again.'));
        waited += 200;
        setTimeout(check, 200);
      })();
    });
  }

  function interceptAtcButton() {
    var originalFetch = window.fetch;

    window.fetch = function (url, options) {
      var isAdd = typeof url === 'string' &&
                  url.indexOf('/cart/add') !== -1 &&
                  options && options.body instanceof FormData;

      if (!isAdd || !S.giftOpen || options.body.get('properties[_lgb_gift]')) {
        return originalFetch.apply(this, arguments);
      }

      options.body.set('properties[Gift Presentation]', 'Yes');

      var self = this, args = arguments;

      // Bow FIRST, then main product, so the popup and cart count include both
      return ensureGiftVariant()
        .then(function () {
          var bow = new FormData();
          bow.append('id', CFG.giftVariantId);
          bow.append('quantity', 1);
          bow.append('properties[_lgb_gift]', '1');
          if (D.giftMsg && D.giftMsg.value.trim()) {
            bow.append('properties[Gift Message]', D.giftMsg.value.trim());
          }
          return originalFetch('/cart/add.js', {
            method:  'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
            body:    bow,
          });
        })
        .then(function (r) {
          return r.json().then(function (j) {
            if (!r.ok || j.status) {
              throw new Error(j.description || j.message || 'Gift presentation could not be added. Please try again.');
            }
            return j;
          });
        })
        .then(function (bowItem) {
          return originalFetch.apply(self, args).then(function (resp) {
            // Main product failed (e.g. out of stock): remove the orphan bow
            if (!resp.ok && bowItem && bowItem.key) {
              originalFetch('/cart/change.js', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body:    JSON.stringify({ id: bowItem.key, quantity: 0 }),
              });
            }
            return resp;
          });
        })
        .catch(function (err) {
          // Switch gift off so the next tap adds the product normally
          openGift(false);
          alert('Sorry, we could not add gift presentation right now, so it has been removed. Please tap Add to Bag again to continue.');
          console.error('[LGB-Gift] gift add failed:', err && err.message);
          throw err;
        });
    };
  }

  /* ── EVENTS ── */
  function bindEvents() {
    if (D.hdrGift) {
      D.hdrGift.addEventListener('click', function (e) {
        if (e.target.closest('.lgb__textlink')) return;
        openGift(!S.giftOpen);
      });
      D.hdrGift.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openGift(!S.giftOpen); }
      });
    }
    if (D.ctaGift) {
      D.ctaGift.addEventListener('click', function (e) {
        e.stopPropagation();
        openGift(true);
      });
    }
    if (D.giftMsg && D.charN) {
      D.giftMsg.addEventListener('input', function () {
        D.charN.textContent = D.giftMsg.value.length;
      });
    }
  }

  function openGift(on) {
    S.touched = true;
    S.giftOpen = on;
    if (D.bodyGift) {
      D.bodyGift.classList.toggle('open', on);
      D.bodyGift.setAttribute('aria-hidden', String(!on));
    }
    if (D.hdrGift)  D.hdrGift.setAttribute('aria-expanded', String(on));
    if (D.circGift) D.circGift.classList.toggle('on', on);
    if (D.giftMsg) {
      D.giftMsg.disabled = !on;
      if (!on) D.giftMsg.value = '';
    }
    if (!on && D.charN) D.charN.textContent = '0';
    updateSummary();
  }

  /* ── SUMMARY + ATC BUTTON PRICE ── */
  function updateSummary() {
    var qty  = currentQty();
    var line = S.basePricePence * qty;

    if (D.sumBasePrice) D.sumBasePrice.textContent = moneyGBP(line);
    if (D.sumBaseQty)   D.sumBaseQty.textContent   = qty > 1 ? ' × ' + qty : '';

    if (D.sumGift) D.sumGift.style.display = S.giftOpen ? '' : 'none';

    var total = line + (S.giftOpen ? CFG.giftPricePence : 0);
    if (D.sumTotal) D.sumTotal.textContent = moneyGBP(total);
    updateAtcBtn(total);
  }

  function updateAtcBtn(totalPence) {
    if (!S.touched) return;
    var btn = atcButton();
    if (!btn || btn.disabled) return;
    var span = btn.querySelector('span:not(.loading__spinner):not(.loading-overlay__spinner)') || btn;
    var base = span.textContent.replace(PRICE_SUFFIX, '').trim();
    var want = base + ' — ' + moneyGBP(totalPence);
    if (span.textContent !== want) span.textContent = want;
  }

  /* ── EXPRESS BUTTONS (Buy with Shop / Buy it now / wallets) ──
     They post the form straight to checkout and never hit the
     Add to Bag intercept. While gift is ON, catch the click,
     add product + bow via the normal flow, then go to Shop Pay
     or checkout. */
  var expressBusy = false;

  function interceptExpressButtons() {
    document.addEventListener('click', function (e) {
      if (!S.giftOpen) return;
      var path = e.composedPath ? e.composedPath() : [];
      var hit = path.some(function (n) {
        return n && n.classList && n.classList.contains('shopify-payment-button');
      });
      if (!hit) return;
      // Buy with Shop -> straight to the Shop Pay page; other buttons -> checkout
      var isShopPay = path.some(function (n) {
        return n && n.tagName === 'SHOP-PAY-WALLET-BUTTON';
      });
      var form = atcForm();
      if (!form) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (expressBusy) return;
      expressBusy = true;

      // Goes through the patched fetch above, so the bow + message are added
      window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: new FormData(form),
      })
        .then(function (r) {
          if (r.ok) {
            window.location.href = isShopPay ? '/checkout?payment=shop_pay' : '/checkout';
            return;
          }
          return r.json().then(function (j) {
            expressBusy = false;
            alert(j.description || j.message || 'Sorry, this item could not be added. Please try again.');
          });
        })
        .catch(function (err) {
          expressBusy = false;
          console.error('[LGB-Gift] express checkout failed:', err && err.message);
        });
    }, true);
  }

  /* ── FETCH GIFT PRODUCT VARIANT ── */
  async function fetchGiftProduct() {
    try {
      var res = await fetch('/products/' + CFG.giftHandle + '.js');
      if (!res.ok) throw new Error('HTTP ' + res.status + ' - handle "' + CFG.giftHandle + '" not found');
      var data = await res.json();
      CFG.giftVariantId  = data.variants[0].id;
      CFG.giftPricePence = data.variants[0].price;
      if (D.giftBadge)    D.giftBadge.textContent    = '+' + moneyGBP(CFG.giftPricePence);
      if (D.sumGiftPrice) D.sumGiftPrice.textContent = moneyGBP(CFG.giftPricePence);
      updateSummary();
    } catch (e) {
      console.error('[LGB-Gift] fetchGiftProduct FAILED:', e.message);
    }
  }

  /* ── UTILS ── */
  function moneyGBP(p) { return '£' + (p / 100).toFixed(2); }

  /* ── BOOT ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();